import axios from 'axios';

// Helper function to get full image URL
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${process.env.REACT_APP_API_URL}${imagePath}`;
};

// Lookup service for API operations
export const lookupService = {
  fetchItems: async (type) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/lookups/${type}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      throw error;
    }
  },

  createItem: async (type, data) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/lookups/${type}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      throw error;
    }
  },

  updateItem: async (type, id, data) => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/lookups/${type}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating ${type}:`, error);
      throw error;
    }
  },

  deleteItem: async (type, id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/lookups/${type}/${id}`);
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      throw error;
    }
  },

  uploadImage: async (file) => {
    try {
      // console.log('Starting image upload for file:', file.name);
      const formData = new FormData();
      formData.append('image', file);

      const uploadUrl = `${process.env.REACT_APP_API_URL}/api/upload/quiz`;
      // console.log('Upload URL:', uploadUrl);

      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // console.log('Upload response:', response.data);
      return response.data.imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  }
};

// Data transformation utilities
export const transformDataForServer = (data, type) => {
  // console.log('Transforming data for server:', { data, type });

  // Transform the form data to match server expectations
  const transformed = { ...data };

  // Ensure status is a number
  if (transformed.status !== undefined) {
    transformed.status = Number(transformed.status);
  }

  // Map field names based on type - backend expects { name, description, status, ... }
  let serverData;

  if (type === 'difficulty') {
    serverData = {
      name: transformed.level_name,
      description: transformed.description || '', // Use description field
      criteria_text: transformed.criteria_text || '', // Detailed AI criteria text
      display_image: transformed.display_image || '', // Include display_image field
      status: transformed.status,
      base_time: Number(transformed.base_time) || 30,
      base_points: Number(transformed.base_points) || 1000,
      quick_time_bonus_multiplier: Number(transformed.quick_time_bonus_multiplier) || 2.0,
      late_time_bonus_multiplier: Number(transformed.late_time_bonus_multiplier) || 0.5,
      wrong_time_bonus_multiplier: Number(transformed.wrong_time_bonus_multiplier) || -1.0,
      wrong_penalty_time_multiplier: Number(transformed.wrong_penalty_time_multiplier) || 2.0
    };
  } else if (type === 'question-type' || type === 'answer-type') {
    serverData = {
      name: transformed.type_name,
      description: transformed.display_name,
      status: transformed.status
    };
  } else if (type === 'question-mood') {
    serverData = {
      name: transformed.mood_name,
      description: transformed.display_name,
      status: transformed.status
    };
  } else if (type === 'question-topic') {
    serverData = {
      name: transformed.topic_name,
      description: transformed.display_name,
      status: transformed.status
    };
  } else if (type === 'question-subtopic') {
    serverData = {
      name: transformed.subtopic_name,
      description: transformed.display_name,
      status: transformed.status,
      topic_type_id: Number(transformed.topic_type_id) || null
    };
  } else if (type === 'question-bank') {
    serverData = {
      name: transformed.bank_name,
      description: transformed.bank_name, // Using bank_name as description
      status: transformed.status
    };
  } else if (type === 'games') {
    serverData = {
      name: transformed.game_name,
      description: transformed.game_description,
      status: transformed.status
    };
  } else if (type === 'games-datestamps') {
    serverData = {
      game_id: transformed.game_id,
      name: transformed.date_stamp_name,
      description: transformed.description,
      status: transformed.status
    };
  } else {
    // Fallback for unknown types
    serverData = {
      name: transformed.name,
      description: transformed.description,
      status: transformed.status
    };
  }

  // console.log('Transformed server data:', serverData);
  return serverData;
};

// Field configuration utilities
export const getIdField = (type) => {
  const idFields = {
    'difficulty': 'difficulty_id',
    'question-type': 'question_type_id',
    'answer-type': 'answer_type_id',
    'question-mood': 'mood_type_id',
    'question-topic': 'topic_type_id',
    'question-subtopic': 'subtopic_type_id',
    'question-bank': 'bank_id',
    'games': 'games_lookup_id',
    'games-datestamps': 'games_datestamps_lookups_id'
  };
  return idFields[type];
};

export const getFormFields = (lookupType) => {
  const fieldConfigs = {
    'difficulty': [
      { key: 'level_name', label: 'Level Name', required: true, placeholder: 'e.g., Easy, Medium, Hard' },
      { key: 'base_time', label: 'Base Time (seconds)', type: 'number', required: true, min: 1, placeholder: 'e.g., 30' },
      { key: 'base_points', label: 'Base Points', type: 'number', required: true, min: 1, defaultValue: 1000, placeholder: 'e.g., 1000' },
      { key: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Optional short description...' },
      {
        key: 'criteria_text',
        label: 'Criteria Text (AI Prompt)',
        type: 'textarea',
        required: false,
        placeholder: 'Optional: paste the detailed 20-point (or any) criteria the AI should follow for this difficulty.'
      },
      { key: 'quick_time_bonus_multiplier', label: 'Quick Time Bonus Multiplier', type: 'number', required: true, min: 0, step: 0.1, defaultValue: 2.0, placeholder: 'e.g., 2.0' },
      { key: 'late_time_bonus_multiplier', label: 'Late Time Bonus Multiplier', type: 'number', required: true, step: 0.1, defaultValue: 0.5, placeholder: 'e.g., 0.5' },
      { key: 'wrong_time_bonus_multiplier', label: 'Wrong Time Bonus Multiplier', type: 'number', required: true, step: 0.1, defaultValue: -1.0, placeholder: 'e.g., -1.0' },
      { key: 'wrong_penalty_time_multiplier', label: 'Wrong Penalty Time Multiplier', type: 'number', required: true, min: 0, step: 0.1, defaultValue: 2.0, placeholder: 'e.g., 2.0' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ],
    'question-type': [
      { key: 'type_name', label: 'Type Name', required: true, placeholder: 'e.g., text, text_image' },
      { key: 'display_name', label: 'Display Name', required: true, placeholder: 'e.g., Text Only, Text with Image' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ],
    'answer-type': [
      { key: 'type_name', label: 'Type Name', required: true, placeholder: 'e.g., multiple_choice, true_false' },
      { key: 'display_name', label: 'Display Name', required: true, placeholder: 'e.g., Multiple Choice, True/False' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ],
    'question-mood': [
      { key: 'mood_name', label: 'Mood Name', required: true, placeholder: 'e.g., upbeat, calm, energetic' },
      { key: 'display_name', label: 'Display Name', required: true, placeholder: 'e.g., Upbeat, Calm, Energetic' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ],
    'question-topic': [
      { key: 'topic_name', label: 'Topic Name', required: true, placeholder: 'e.g., music_theory, pop_culture, history' },
      { key: 'display_name', label: 'Display Name', required: true, placeholder: 'e.g., Music Theory, Pop Culture, History' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ],
    'question-subtopic': [
      { key: 'topic_type_id', label: 'Topic', type: 'select', required: true, placeholder: 'Select a topic', optionsType: 'question-topic' },
      { key: 'subtopic_name', label: 'Sub-Topic Name', required: true, placeholder: 'e.g., world_war, ancient_history' },
      { key: 'display_name', label: 'Display Name', required: true, placeholder: 'e.g., World War, Ancient History' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ],
    'question-bank': [
      { key: 'bank_name', label: 'Bank Name', required: true, placeholder: 'e.g., General Knowledge, Music Theory, Pop Culture' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ],
    'games': [
      { key: 'game_name', label: 'Game Name', required: true, placeholder: 'e.g., Quiz, Live Quiz, Trivia' },
      { key: 'game_description', label: 'Description', type: 'textarea', required: false, placeholder: 'Optional description...' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ],
    'games-datestamps': [
      { key: 'game_id', label: 'Game', type: 'select', required: true, placeholder: 'Select a game' },
      { key: 'date_stamp_name', label: 'Date Stamp Name', required: true, placeholder: 'e.g., quiz_welcome, quiz_config, quiz_rules' },
      { key: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Optional description...' },
      { key: 'status', label: 'Status', type: 'select', required: true }
    ]
  };
  return fieldConfigs[lookupType] || [];
};
